import React from "react";
import { Button } from "~/components/ui/button";
import { TextOverflow } from "~/components/ui/text-overflow";

export interface PresetPrompt {
  /**
   * Display text shown on the button
   */
  title: string;
  /**
   * Actual prompt text sent to the assistant
   */
  prompt: string;
}

export interface PresetPromptsProps {
  /**
   * Callback when a preset prompt is clicked
   */
  onPromptClick: (prompt: string) => void;
  /**
   * Array of preset prompts to display as pills
   */
  prompts?: PresetPrompt[];
  /**
   * Whether the preset buttons should be disabled
   */
  disabled?: boolean;
}

const DEFAULT_PRESET_PROMPTS: PresetPrompt[] = [
  {
    title: "Tell me about this dataset",
    prompt:
      "Tell me about the data in this dataset using only the metadata available to you from the data catalog.",
  },
  {
    title: "Preview a table",
    prompt: "Show me the top 10 rows of one of the tables.",
  },
  {
    title: "Generate example histogram",
    prompt: "Create me a histogram of a categorical column from a table of your choice",
  },
];

export function PresetPrompts({
  onPromptClick,
  prompts = DEFAULT_PRESET_PROMPTS,
  disabled = false,
}: PresetPromptsProps) {
  const splitIndex = prompts.length <= 3 ? prompts.length : Math.floor(prompts.length / 2);
  const topRow = prompts.slice(0, splitIndex);
  const bottomRow = prompts.slice(splitIndex);

  return (
    <div className="animate-in fade-in duration-200 px-4">
      <div className="flex flex-col gap-2 max-w-[900px] mx-auto mb-4">
        <div className="flex flex-row gap-2">
          {topRow.map((presetPrompt) => (
            <Button
              key={presetPrompt.title}
              variant="outline"
              size="sm"
              onClick={() => onPromptClick(presetPrompt.prompt)}
              disabled={disabled}
              className="text-sm flex-1 min-w-0"
            >
              <TextOverflow autoTooltip delayDuration={700}>
                {presetPrompt.title}
              </TextOverflow>
            </Button>
          ))}
        </div>
        {bottomRow.length > 0 && (
          <div className="flex flex-row gap-2">
            {bottomRow.map((presetPrompt) => (
              <Button
                key={presetPrompt.title}
                variant="outline"
                size="sm"
                onClick={() => onPromptClick(presetPrompt.prompt)}
                disabled={disabled}
                className="text-sm flex-1 min-w-0"
              >
                <TextOverflow autoTooltip delayDuration={700}>
                  {presetPrompt.title}
                </TextOverflow>
              </Button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
